import React, { useCallback, useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { CONTINUATION_TYPES, FORM_TYPES } from '../../../../constants';
import { useFormValidation } from '../../../../hooks';
import {
	buildFormSections,
	parseFormSections,
	getAdvancedSettings,
	missingFormTranslations,
	tcFormValidators,
} from '../../../../utils';

import {
	FIELD_EDITOR_FIELDS,
	FORM_EDITOR_FIELDS,
	OPTION_EDITOR_FIELDS,
	SECTION_EDITOR_FIELDS,
} from './constants';
import {
	formEditorDataReducers,
	FORM_EDITOR_DATA_DISPATCH,
} from './reducer';

/**
 * Initializes and manages form data for editing a form.
 *
 * @param {Object}   props                    The props from the consuming component.
 * @param {Object}   props.form               The initial data for the form.
 * @param {Object[]} props.tickets            The list of tickets associated with the product.
 * @param {Object[]} props.ticketGroups       The list of ticket groups associated with the product.
 * @param {String}   props.defaultLanguage    The default language for the form.
 * @param {String}   props.preferredLanguage  The preferred language for the current user.
 * @param {Function} props.onLanguageChange   Callback to handle language changes.
 * @param {Function} props.onSave             Callback to handle form submission.
 * @param {Function} props.onWidthChange      Callback to handle width changes.
 *
 * @returns {Object} result
 * @returns {Object} result.formData         The form state.
 * @returns {Object} result.inputValidation  The input validation state.
 * @returns {Object} result.actions          The set of actions for updating the form state.
 *
 * @returns {Function} result.actions.submitForm
 * Handles the form submission.
 *
 * @returns {Function} result.actions.updateForm
 * Handles changes to HTML form inputs.
 *
 * @returns {Function} result.actions.updateLanguage
 * Handles changes to Language select input.
 *
 * @returns {Function} result.actions.addTranslation
 * Adds new translation to the state.
 *
 * @returns {Function} result.actions.deleteTranslation
 * Removes translation from the state.
 *
 * @returns {Function} result.actions.generateFormPreview
 * Callback function to convert the form data to the final form to preview.
 *
 * @returns {Object} result.actions.sections  Actions related to form sections.
 *
 * @returns {Function} result.actions.sections.add
 * Callback function to add a new form section.
 *
 * @returns {Function} result.actions.sections.delete
 * Callback function to delete a form section.
 *
 * @returns {Function} result.actions.sections.move
 * Callback function to move a form section.
 *
 * @returns {Function} result.actions.sections.update
 * Callback function to update a form section.
 *
 * @returns {Function} result.actions.sections.updateAdvancedSetting
 * Callback function to update the advanced settings for a form section.
 *
 * @returns {Object} result.actions.fields  Actions related to form fields.
 *
 * @returns {Function} result.actions.fields.add
 * Callback function to add a new form field.
 *
 * @returns {Function} result.actions.fields.clone
 * Callback function to clone a form field.
 *
 * @returns {Function} result.actions.fields.delete
 * Callback function to delete a form field.
 *
 * @returns {Function} result.actions.fields.move
 * Callback function to move a form field.
 *
 * @returns {Function} result.actions.fields.update
 * Callback function to update a form field.
 *
 * @returns {Object} result.actions.options  Actions related to form field options.
 *
 * @returns {Function} result.actions.options.add
 * Callback function to add a new form field option.
 *
 * @returns {Function} result.actions.options.delete
 * Callback function to delete a form field option.
 *
 * @returns {Function} result.actions.options.move
 * Callback function to move a form field option.
 *
 * @returns {Function} result.actions.options.update
 * Callback function to update a form field option.
 */
const useFormEditorData = (props) => {

	const {
		tickets,
		ticketGroups,
		defaultLanguage,
		preferredLanguage,
		onLanguageChange,
		onWidthChange,
		onSave,
	} = props;

	const [formData, setFormData] = useState(() => buildInitialValues(props));

	const [hasSubmitted, setHasSubmitted] = useState(false);

	const { inputValidation, validateFormData } = useFormValidation({
		[FORM_EDITOR_FIELDS.SECTIONS]:
			[tcFormValidators.arrayContainingObjects('id', {
				[SECTION_EDITOR_FIELDS.FIELDS]:
					[tcFormValidators.arrayContainingObjects('id', {
						[FIELD_EDITOR_FIELDS.OPTIONS]: [
							tcFormValidators.arrayContainingObjects('id', {
								[OPTION_EDITOR_FIELDS.VALUE]: [tcFormValidators.required],
								[OPTION_EDITOR_FIELDS.CONTENTS]:
									[tcFormValidators.arrayContainingObjects('language', {
										[OPTION_EDITOR_FIELDS.LABEL]: [tcFormValidators.required],
									})],
							}),
						],
						[FIELD_EDITOR_FIELDS.CONTENTS]:
							[tcFormValidators.arrayContainingObjects('language', {
								[FIELD_EDITOR_FIELDS.TITLE]: [tcFormValidators.required],
							})],
					})],
				[SECTION_EDITOR_FIELDS.CONTENTS]:
					[tcFormValidators.arrayContainingObjects('language', {
						[SECTION_EDITOR_FIELDS.TITLE]: [tcFormValidators.required],
					})],
			})],
	});

	// Re-validate the form data on changes (but only after first form submission attempt)
	useEffect(() => {

		if (hasSubmitted) {
			validateFormData(formData);
		}

	}, [formData, hasSubmitted, validateFormData]);

	/**
	 * Adds a new section to the form.
	 */
	const addSection = useCallback(() => {

		const reducer = formEditorDataReducers[FORM_EDITOR_DATA_DISPATCH.ADD_FORM_SECTION];

		setFormData((prev) => reducer(prev, { tickets, ticketGroups, defaultLanguage }));

	}, [tickets, ticketGroups, defaultLanguage]);

	/**
	 * Deletes a section from the form.
	 *
	 * @param {String} sectionID  The section ID.
	 */
	const deleteSection = useCallback((sectionID) => {

		const reducer = formEditorDataReducers[FORM_EDITOR_DATA_DISPATCH.DELETE_FORM_SECTION];

		setFormData((prev) => reducer(prev, { sectionID }));

	}, []);

	/**
	 * Moves a section in the form.
	 *
	 * @param {String} sectionID  The section ID.
	 * @param {Object} prevItem   The item to place the section after.
	 * @param {Object} nextItem   The item to place the section before.
	 */
	const moveSection = useCallback((sectionID, prevItem, nextItem) => {

		const reducer = formEditorDataReducers[FORM_EDITOR_DATA_DISPATCH.MOVE_FORM_SECTION];

		setFormData((prev) => reducer(prev, { sectionID, prevItem, nextItem }));

	}, []);

	/**
	 * Updates a section in the form.
	 *
	 * @param {String} sectionID  The section ID.
	 * @param {String} fieldName   The name of the field to update for the section.
	 * @param {String} fieldValue  The new value of the field for the section.
	 */
	const updateSection = useCallback((sectionID, fieldName, fieldValue) => {

		let type;

		switch (fieldName) {

		case SECTION_EDITOR_FIELDS.CONTINUATION_TYPE:
		case SECTION_EDITOR_FIELDS.CONTINUATION_SECTION_ID:

			type = FORM_EDITOR_DATA_DISPATCH.UPDATE_FORM_SECTION;
			break;

		case SECTION_EDITOR_FIELDS.TITLE:
		case SECTION_EDITOR_FIELDS.DESCRIPTION:

			type = FORM_EDITOR_DATA_DISPATCH.UPDATE_FORM_SECTION_CONTENTS;
			break;

		default:

			console.error('updateSection: Invalid action type for form editor data reducer');
			return;

		}

		const reducer = formEditorDataReducers[type];

		setFormData((prev) => reducer(prev, { sectionID, fieldName, fieldValue }));

	}, []);

	/**
	 * Updates the advanced settings for a section in the form.
	 *
	 * @param {String} settingID   The advanced setting ID.
	 * @param {String} fieldName   The name of the field to update for the advanced setting.
	 * @param {String} fieldValue  The new value of the field for the advanced setting.
	 */
	const updateSectionAdvancedSetting = useCallback((settingID, fieldName, fieldValue) => {

		const reducer = formEditorDataReducers[
			FORM_EDITOR_DATA_DISPATCH.CHANGE_FORM_SECTION_ADVANCED_SETTINGS
		];

		setFormData((prev) => reducer(prev, { settingID, fieldName, fieldValue }));

	}, []);

	/**
	 * Adds a new field to the form.
	 */
	const addField = useCallback((type, sectionID) => {

		const reducer = formEditorDataReducers[FORM_EDITOR_DATA_DISPATCH.ADD_FORM_FIELD];

		setFormData((prev) => reducer(prev, { type, sectionID, defaultLanguage }));

	}, [defaultLanguage]);

	/**
	 * Updates a field in the form.
	 *
	 * @param {String} fieldID     The field ID.
	 * @param {String} fieldName   The name of the field to update for the field.
	 * @param {String} fieldValue  The new value for the field.
	 */
	const updateField = useCallback((fieldID, fieldName, fieldValue) => {

		let type;

		switch (fieldName) {

		case FIELD_EDITOR_FIELDS.CONDITIONAL:
		case FIELD_EDITOR_FIELDS.FILTER:
		case FIELD_EDITOR_FIELDS.PRIVACY:
		case FIELD_EDITOR_FIELDS.REQUIRED:
		case FIELD_EDITOR_FIELDS.SUBTYPE:
		case FIELD_EDITOR_FIELDS.TYPE:
		case FIELD_EDITOR_FIELDS.OPTIONS:

			type = FORM_EDITOR_DATA_DISPATCH.UPDATE_FORM_FIELD;
			break;

		case FIELD_EDITOR_FIELDS.TITLE:
		case FIELD_EDITOR_FIELDS.PLACEHOLDER:
		case FIELD_EDITOR_FIELDS.HELP:
		case FIELD_EDITOR_FIELDS.SUBMISSION_FEEDBACK_CORRECT:
		case FIELD_EDITOR_FIELDS.SUBMISSION_FEEDBACK_INCORRECT:

			type = FORM_EDITOR_DATA_DISPATCH.UPDATE_FORM_FIELD_CONTENTS;
			break;

		default:

			console.error('updateField: Invalid action type for form editor data reducer');
			return;

		}

		const reducer = formEditorDataReducers[type];

		setFormData((prev) => reducer(prev, { fieldID, fieldName, fieldValue }));

	}, []);

	/**
	 * Clones a field in the form.
	 *
	 * @param {String} fieldID  The field ID.
	 */
	const cloneField = useCallback((fieldID) => {

		const reducer = formEditorDataReducers[FORM_EDITOR_DATA_DISPATCH.CLONE_FORM_FIELD];

		setFormData((prev) => reducer(prev, { fieldID }));

	}, []);

	/**
	 * Deletes a field from the form.
	 *
	 * @param {String} fieldID  The field ID.
	 */
	const deleteField = useCallback((fieldID) => {

		const reducer = formEditorDataReducers[FORM_EDITOR_DATA_DISPATCH.DELETE_FORM_FIELD];

		setFormData((prev) => reducer(prev, { fieldID }));

	}, []);

	/**
	 * Moves a field in the form.
	 *
	 * @param {String} fieldID    The field ID.
	 * @param {Object} prevItem   The item to place the field after.
	 * @param {Object} nextItem   The item to place the field before.
	 */
	const moveField = useCallback((fieldID, prevItem, nextItem) => {

		const reducer = formEditorDataReducers[FORM_EDITOR_DATA_DISPATCH.MOVE_FORM_FIELD];

		setFormData((prev) => reducer(prev, { fieldID, prevItem, nextItem }));

	}, []);

	/**
	 * Adds a new option to a field in the form.
	 *
	 * @param {String} fieldID  The field ID.
	 */
	const addOption = useCallback((fieldID) => {

		const reducer = formEditorDataReducers[FORM_EDITOR_DATA_DISPATCH.ADD_FORM_FIELD_OPTION];

		setFormData((prev) => reducer(prev, { fieldID, defaultLanguage }));

	}, [defaultLanguage]);

	/**
	 * Updates an option for a field in the form.
	 *
	 * @param {String} fieldID     The field ID.
	 * @param {String} optionID    The option ID.
	 * @param {String} fieldName   The name of the field to update for the option.
	 * @param {String} fieldValue  The new value for the option.
	 */
	const updateOption = useCallback((fieldID, optionID, fieldName, fieldValue) => {

		let type;

		switch (fieldName) {

		case OPTION_EDITOR_FIELDS.VALUE:
		case OPTION_EDITOR_FIELDS.IS_CORRECT_ANSWER:
		case OPTION_EDITOR_FIELDS.CONTINUATION_TYPE:
		case OPTION_EDITOR_FIELDS.CONTINUATION_SECTION_ID:

			type = FORM_EDITOR_DATA_DISPATCH.UPDATE_FORM_FIELD_OPTION;
			break;

		case OPTION_EDITOR_FIELDS.LABEL:

			type = FORM_EDITOR_DATA_DISPATCH.UPDATE_FORM_FIELD_OPTION_CONTENTS;
			break;

		default:

			console.error('updateOption: Invalid action type for form editor data reducer');
			return;

		}

		const reducer = formEditorDataReducers[type];

		setFormData((prev) => reducer(prev, { fieldID, optionID, fieldName, fieldValue }));

	}, []);

	/**
	 * Deletes an option from a field in the form.
	 *
	 * @param {String} fieldID   The field ID.
	 * @param {String} optionID  The option ID.
	 */
	const deleteOption = useCallback((fieldID, optionID) => {

		const reducer = formEditorDataReducers[FORM_EDITOR_DATA_DISPATCH.DELETE_FORM_FIELD_OPTION];

		setFormData((prev) => reducer(prev, { fieldID, optionID }));

	}, []);

	/**
	 * Moves an option for a field in the form.
	 *
	 * @param {String} fieldID    The field ID.
	 * @param {String} optionID   The option ID.
	 * @param {Object} prevItem   The item to place the field after.
	 * @param {Object} nextItem   The item to place the field before.
	 */
	const moveOption = useCallback((fieldID, optionID, prevItem, nextItem) => {

		const reducer = formEditorDataReducers[FORM_EDITOR_DATA_DISPATCH.MOVE_FORM_FIELD_OPTION];

		setFormData((prev) => reducer(prev, { fieldID, optionID, prevItem, nextItem }));

	}, []);

	/**
	 * Handles changes to the form input.
	 */
	const updateForm = useCallback((fieldName, fieldValue) => {

		// Notify the callback listener in case the form width changes
		if (fieldName === FORM_EDITOR_FIELDS.WIDTH) {

			onWidthChange(buildFormData({
				...formData,
				[FORM_EDITOR_FIELDS.WIDTH]: parseInt(fieldValue, 10),
				// Return content in the user's current language
				[FORM_EDITOR_FIELDS.LANGUAGE]: preferredLanguage,
			}, defaultLanguage));

		}

		setFormData((prev) => ({
			...prev,
			[fieldName]: fieldValue,
		}));

	}, [defaultLanguage, preferredLanguage, formData, onWidthChange]);

	/**
	 * Handles change to language select input.
	 *
	 * If the translation for the selected language has not already been loaded,
	 * will fetch the form|form-section|form-field|form-field-option content for
	 * the selected language.
	 *
	 * @param {String} fieldName  The name of the field to update.
	 * @param {String} fieldValue  The new value of the field.
	 */
	const updateLanguage = useCallback(async (fieldName, fieldValue) => {

		const isValid = validateFormData(formData);

		// Prevent user from switching languages until form errors are fixed
		if (!isValid) {
			setHasSubmitted(true);
			return;
		}

		// Reset submission state for new translation form view
		setHasSubmitted(false);

		const reducer = formEditorDataReducers[FORM_EDITOR_DATA_DISPATCH.UPDATE_FORM_LANGUAGE];

		const updatedFormData = await reducer(formData, {
			fieldValue,
			onLanguageChange,
		});

		setFormData(updatedFormData);

	}, [formData, validateFormData, onLanguageChange]);

	/**
	 * Adds new translation to current multi-language contents state for the form.
	 */
	const addTranslation = useCallback(() => {

		const reducer = formEditorDataReducers[FORM_EDITOR_DATA_DISPATCH.ADD_TRANSLATION];

		setFormData((prev) => reducer(prev));

	}, []);

	/**
	 * Removes translation from current multi-language contents state and
	 * adds to list of contents to delete when form is submitted.
	 */
	const deleteTranslation = useCallback(() => {

		const reducer = formEditorDataReducers[FORM_EDITOR_DATA_DISPATCH.DELETE_TRANSLATION];

		setFormData((prev) => reducer(prev));

	}, []);

	/**
	 * Generates a preview of the final form data when it is in the embedded view.
	 *
	 * @returns {Object} result  The form.
	 */
	const generateFormPreview = useCallback(
		() => buildFormData(formData, defaultLanguage),
		[formData, defaultLanguage],
	);

	/**
	 * Validates the form data and saves the final results to the database.
	 *
	 * @returns {Boolean}  True if form was successfully submitted, false otherwise.
	 */
	const submitForm = useCallback(async () => {

		setHasSubmitted(true);

		const isValid = validateFormData(formData);

		// Prevent user from saving changes until form errors are fixed
		if (!isValid) {

			setTimeout(scrollToFirstError, 250);
			return false;

		}

		// Persist the updates
		await onSave(buildFormData({
			...formData,
			// Return content in the user's current language
			[FORM_EDITOR_FIELDS.LANGUAGE]: preferredLanguage,
		}, defaultLanguage));

		return true;

	}, [defaultLanguage, preferredLanguage, formData, validateFormData, onSave]);

	return {
		formData,
		inputValidation,
		actions: {
			submitForm,
			updateForm,
			sections: {
				add: addSection,
				delete: deleteSection,
				move: moveSection,
				update: updateSection,
				updateAdvancedSetting: updateSectionAdvancedSetting,
			},
			fields: {
				add: addField,
				clone: cloneField,
				delete: deleteField,
				move: moveField,
				update: updateField,
			},
			options: {
				add: addOption,
				delete: deleteOption,
				move: moveOption,
				update: updateOption,
			},
			updateLanguage,
			addTranslation,
			deleteTranslation,
			generateFormPreview,
		},
	};

};

/**
 * Determines the initial language for the form state.
 *
 * @param {Object} props  The component props.
 *
 * @param {Object} props.form                 The initial data for the form.
 * @param {String} props.defaultLanguage      The default language for the form.
 * @param {String} [props.preferredLanguage]  The preferred language for the current user.
 *
 * @returns {String} result  The initial language value.
 */
function getInitialLanguage(props) {

	const {
		form,
		preferredLanguage,
		defaultLanguage,
	} = props;

	// Check if translations exist in the preferred language
	if (form.id && !missingFormTranslations(form, preferredLanguage)) {
		return preferredLanguage;
	}

	// If nothing exists yet in preferred language, fall back to the default
	return defaultLanguage;

}

/**
 * Builds the initial values for the form data when creating/editing a form.
 *
 * @param {Object} props  The component props.
 *
 * @param {String} props.defaultLanguage      The default language for the form.
 * @param {String} [props.preferredLanguage]  The preferred language for the current user.
 *
 * @param {Object[]} props.tickets           The list of tickets for the associated product.
 * @param {Object[]} props.ticketGroups      The list of ticket groups for the associated product.
 *
 * @param {Object} props.form  The initial data for the form.
 * @param {String} props.form.id  The form ID.
 * @param {String} props.form.type  The form type.
 * @param {Number} props.form.width  The form width.
 * @param {String} props.form.scope   The form scope.
 * @param {String} props.form.scope_id  The form scope ID.
 * @param {Object[]} props.form.form_section_advanced_settings
 * The list of advanced navigation settings for the form.
 *
 * @returns {Object} result  The form data for the form to create/edit.
 * @returns {String} [result.id]  The ID of the form (if any).
 * @returns {String} result.type  The form type.
 * @returns {String} result.language  The currently selected language.
 * @returns {Number} result.width  The form width.
 * @returns {String} result.scope  The form scope.
 * @returns {String} result.scope_id  The form scope ID.
 *
 * @returns {Object[]} result.sections  The list of sections in the form.
 */
function buildInitialValues(props) {

	const { form, defaultLanguage, tickets, ticketGroups } = props;

	const result = {
		[FORM_EDITOR_FIELDS.ID]: form.id,
		[FORM_EDITOR_FIELDS.TYPE]: form.type,
		[FORM_EDITOR_FIELDS.LANGUAGE]: getInitialLanguage(props),
		[FORM_EDITOR_FIELDS.WIDTH]: form.width,
		[FORM_EDITOR_FIELDS.ALLOW_EDITING_SUBMISSIONS]: form.allow_editing_submissions || false,
		[FORM_EDITOR_FIELDS.ALLOW_MULTIPLE_SUBMISSIONS]: form.allow_multiple_submissions || false,
		[FORM_EDITOR_FIELDS.QUIZ_RESULT_PASS_PERCENTAGE]: form.quiz_result_pass_percentage,
		[FORM_EDITOR_FIELDS.QUIZ_RESULT_SHOW_CORRECT_ANSWERS]: form.quiz_result_show_correct_answers,
		[FORM_EDITOR_FIELDS.SCOPE]: form.scope,
		[FORM_EDITOR_FIELDS.SCOPE_ID]: form.scope_id,
	};

	// Set inital quiz pass percentage requirement to 100% if undefined
	if (result[FORM_EDITOR_FIELDS.TYPE] === FORM_TYPES.QUIZ && (
		result[FORM_EDITOR_FIELDS.QUIZ_RESULT_PASS_PERCENTAGE] === null ||
		result[FORM_EDITOR_FIELDS.QUIZ_RESULT_PASS_PERCENTAGE] === undefined)) {

		result[FORM_EDITOR_FIELDS.QUIZ_RESULT_PASS_PERCENTAGE] = 100;

	}

	// Build the ordered list of form sections with nested field data
	result[FORM_EDITOR_FIELDS.SECTIONS] = buildFormSections(form, props);

	// Insert a default form section at the beginning of the list if empty
	if (result[FORM_EDITOR_FIELDS.SECTIONS].length === 0) {

		const defaultSection = {
			[SECTION_EDITOR_FIELDS.ID]: uuid(),
			[SECTION_EDITOR_FIELDS.CONTINUATION_TYPE]: CONTINUATION_TYPES.DEFAULT,
			[SECTION_EDITOR_FIELDS.CONTINUATION_SECTION_ID]: null,
			[SECTION_EDITOR_FIELDS.WEIGHT]: 10,
			[SECTION_EDITOR_FIELDS.FIELDS]: [],
			[SECTION_EDITOR_FIELDS.ADVANCED_SETTINGS]: [],
			[SECTION_EDITOR_FIELDS.CONTENTS]: [{
				language: defaultLanguage,
				[SECTION_EDITOR_FIELDS.TITLE]: form.name,
				[SECTION_EDITOR_FIELDS.DESCRIPTION]: form.description,
			}],
			[SECTION_EDITOR_FIELDS.DELETED_CONTENTS]: [],
		};

		if (tickets && ticketGroups) {

			// Initialize the advanced settings for the default section, if needed.
			defaultSection[SECTION_EDITOR_FIELDS.ADVANCED_SETTINGS] =
				getAdvancedSettings(defaultSection.id, [], tickets, ticketGroups);

		}

		result[FORM_EDITOR_FIELDS.SECTIONS].push(defaultSection);

	}

	return result;

}

/**
 * Converts the format/state expected by the form editor to the final form.
 *
 * @param {object} data  The current state of the form data.
 * @param {string} defaultLanguage  The default language for the form.
 *
 * @returns {object} formData  The form data.
 */
function buildFormData(data, defaultLanguage) {

	// Build the data for the associated form
	const formData = {
		type: data[FORM_EDITOR_FIELDS.TYPE],
		allow_editing_submissions: data[FORM_EDITOR_FIELDS.ALLOW_EDITING_SUBMISSIONS],
		allow_multiple_submissions: data[FORM_EDITOR_FIELDS.ALLOW_MULTIPLE_SUBMISSIONS],
		quiz_result_pass_percentage: data[FORM_EDITOR_FIELDS.QUIZ_RESULT_PASS_PERCENTAGE],
		quiz_result_show_correct_answers: data[FORM_EDITOR_FIELDS.QUIZ_RESULT_SHOW_CORRECT_ANSWERS],
		width: data[FORM_EDITOR_FIELDS.WIDTH],
		scope: data[FORM_EDITOR_FIELDS.SCOPE],
		scope_id: data[FORM_EDITOR_FIELDS.SCOPE_ID],
		form_fields: [],
		form_sections: [],
		form_section_advanced_settings: [],
	};

	if (data[FORM_EDITOR_FIELDS.ID]) {
		formData.id = data[FORM_EDITOR_FIELDS.ID];
	}

	// Parse the ordered list of form sections with nested field data
	const [sections, fields, settings] = parseFormSections(data[FORM_EDITOR_FIELDS.SECTIONS], {
		defaultLanguage,
	});

	// Keep DEPRECATED fields up-to-date with default language for the form

	const defaultSection = sections[0];

	formData.form_contents = defaultSection?.form_section_contents.map((content) => ({
		language: content.language,
		name: content.title,
		description: content.description,
	}));

	formData.deleted_form_contents = defaultSection?.deleted_form_section_contents.map((content) => ({
		language: content.language,
		name: content.title,
		description: content.description,
	}));

	formData.name = defaultSection?.title;
	formData.description = defaultSection?.description;

	formData.form_fields = fields;
	formData.form_sections = sections;
	formData.form_section_advanced_settings = settings;

	return formData;

}

/**
 * Scrolls any validation errors from the form into view.
 *
 * @param {object} errors  The errors in the form.
 *
 * @returns {void}
 */
function scrollToFirstError() {

	try {

		// Get the first error from the form based on the invalid feedback message(s)
		const errorParent =
			document.querySelector('.tc-form-editor .invalid-feedback')?.parentElement;

		// Find the top of the error in the DOM
		const topOfError = errorParent.getBoundingClientRect().top;
		const currentDOMPosition = document.documentElement.scrollTop;
		const offset = 30;
		const errorPosition = topOfError + currentDOMPosition - offset;

		window.scrollTo({ top: errorPosition, behavior: 'smooth' });

	} catch (error) {

		console.error('Error encountered scrolling to form validation output.');

	}

}

export {
	useFormEditorData,
	FIELD_EDITOR_FIELDS,
	FORM_EDITOR_FIELDS,
	OPTION_EDITOR_FIELDS,
	SECTION_EDITOR_FIELDS,
};
